// Quiz.tsx - Updated to pass correct props to QuizResults
import React, { useCallback } from "react";
import QuestionView from "./QuestionView";
import { useMyDialog } from "@/context/MyDialogProvider";
import { QuizResult, Question } from "./types";
import { QuizResults } from "./QuizResults";

interface QuizProps {
  // Quiz data
  title: string;
  description?: string;
  studentName?: string;
  passingPercentage?: number;
  questions: Question[];
  primaryColor?: string;
  showExplanation?: boolean;
  
  // Quiz state from parent
  quizState: {
    currentQuestion: number;
    selected: string | null;
    score: number;
    showAnswer: boolean;
    finished: boolean;
  };
  
  // Computed values from parent
  percentage: number;
  passed: boolean;
  
  // Callbacks
  onSelect: (option: string) => void;
  onNext: () => void;
  onRestart: () => void;
  onComplete?: (result: QuizResult) => void;
  
  // Optional
  onViewQuestion?: (question: Question) => void;
  grade?: string;
  topicIcon?: string;
}

export default function Quiz({
  title,
  description,
  studentName = "Student",
  passingPercentage = 60,
  questions,
  primaryColor = "bg-black",
  showExplanation = true,
  quizState,
  percentage,
  passed,
  onSelect,
  onNext,
  onRestart,
  onComplete,
  onViewQuestion,
  grade,
  topicIcon = "🏆",
}: QuizProps) {
  const { open, closeAll } = useMyDialog();

  const currentQuestion = questions[quizState.currentQuestion];

  // Handle viewing question in dialog
  const handleViewQuestion = useCallback(
    (question: Question) => {
      open({
        title: `Question ${question.id}`,
        size: "lg",
        content: (
          <QuestionView
            question={question}
            currentQuestion={0}
            totalQuestions={1}
            selected={null}
            showAnswer={true}
            score={0}
            primaryColor={primaryColor}
            onSelect={() => {}}
            onNext={() => closeAll()}
            isInDialog={true}
          />
        ),
        onClose: () => {},
      });
    },
    [open, closeAll, primaryColor]
  );

  // If quiz is finished, show results
  if (quizState.finished) {
    return (
      <QuizResults
        title={title}
        studentName={studentName}
        score={quizState.score}
        total={questions.length}
        percentage={percentage}
        passed={passed}
        primaryColor={primaryColor}
        onRestart={onRestart}
        correctAnswers={quizState.score}
        totalQuestions={questions.length}
        grade={grade}
        topicIcon={topicIcon}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <div className="bg-white shadow-xl rounded-3xl border overflow-hidden">
        <QuestionView
          question={currentQuestion}
          currentQuestion={quizState.currentQuestion}
          totalQuestions={questions.length}
          selected={quizState.selected}
          showAnswer={quizState.showAnswer}
          score={quizState.score}
          primaryColor={primaryColor}
          onSelect={onSelect}
          onNext={onNext}
          onViewQuestion={onViewQuestion || handleViewQuestion}
        />
      </div>
    </div>
  );
}